import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import EmojiPicker, { type EmojiClickData } from "emoji-picker-react";
import {
  ArrowLeft,
  CheckCircle2,
  Copy,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  ImagePlus,
  Layers,
  Loader2,
  Menu,
  MessageSquare,
  MoreVertical,
  Phone,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Trash2,
  UploadCloud,
  Video,
  X,
  ZoomIn,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/ui/toast";
import { ConfirmModal } from "../../components/ui/confirm-modal";
import { useNavStore } from "../../components/layout/nav-store";
import { CopyButton } from "../../components/ui/copy-button";
import { Dropdown } from "../../components/ui/dropdown";
import { whatsappApi } from "../admin/whatsapp-api";
import { cn } from "../../lib/utils";
import {
  emptyCard,
  emptyDraft,
  templateVariables,
  templatesApi,
  type CarouselCard,
  type TemplateButton,
  type TemplateDraft,
  type WhatsAppTemplate,
} from "./api";
import { useResolvedMediaUrl } from "./use-resolved-media";

const input =
  "w-full rounded-lg border border-line bg-paper px-3 py-2 text-xs text-ink outline-none transition placeholder:text-ink-3 focus:border-brand dark:bg-[#1f1c1a]";
const primary =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-brand px-4 py-2 text-xs font-semibold text-white shadow-xs transition hover:bg-brand-hover disabled:opacity-50";
const secondary =
  "inline-flex items-center gap-2 rounded-lg border border-line bg-paper px-3 py-2 text-xs font-semibold text-ink shadow-2xs transition hover:bg-soft disabled:opacity-50 dark:bg-[#1f1c1a]";
const label = "block text-xs font-semibold text-ink-2";
const section =
  "rounded-xl border border-line/60 bg-paper-card p-4 shadow-card dark:bg-[#292524] dark:shadow-card-dark sm:p-5";

const BUTTON_TYPES = ["QUICK_REPLY", "URL", "PHONE_NUMBER", "TEMPLATE"] as const;
const MAX_HEADER_MEDIA = 1;

type MediaItem = { url: string; kind: "image" | "video" };
const kindOfUrl = (url: string): "image" | "video" => (/\.mp4($|\?)/i.test(url) ? "video" : "image");

// Recover the original filename from an S3 key shaped like
// ".../<32-hex-unique>_<original-name>"
const fileNameFromUrl = (url: string): string => {
  try {
    const last = decodeURIComponent(new URL(url).pathname.split("/").pop() || "");
    return last.replace(/^[0-9a-f]{32}_/, "");
  } catch {
    return "";
  }
};

function FormattedWhatsAppText({ text }: { text: string }) {
  if (!text) {
    return <span className="text-ink-3 italic">Your message appears here...</span>;
  }
  const lines = text.split("\n");
  return (
    <span className="whitespace-pre-wrap break-words leading-relaxed text-[13px] text-ink">
      {lines.map((line, lIdx) => {
        const parts = line.split(/(\*[^*]+\*|_[^_]+_|~[^~]+~|```[^`]+```|\{\{\d+\}\})/g);
        return (
          <span key={lIdx}>
            {parts.map((part, pIdx) => {
              if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
                return (
                  <strong key={pIdx} className="font-bold">
                    {part.slice(1, -1)}
                  </strong>
                );
              }
              if (part.startsWith("_") && part.endsWith("_") && part.length > 2) {
                return (
                  <em key={pIdx} className="italic">
                    {part.slice(1, -1)}
                  </em>
                );
              }
              if (part.startsWith("~") && part.endsWith("~") && part.length > 2) {
                return (
                  <del key={pIdx} className="line-through opacity-75">
                    {part.slice(1, -1)}
                  </del>
                );
              }
              if (part.startsWith("```") && part.endsWith("```") && part.length > 6) {
                return (
                  <code
                    key={pIdx}
                    className="rounded bg-black/10 px-1 py-0.5 font-mono text-[11px] dark:bg-white/10"
                  >
                    {part.slice(3, -3)}
                  </code>
                );
              }
              if (/^\{\{\d+\}\}$/.test(part)) {
                return (
                  <span
                    key={pIdx}
                    className="mx-0.5 rounded bg-blue-100 px-1.5 py-0.2 font-mono text-[11px] font-semibold text-blue-800 dark:bg-blue-950/60 dark:text-blue-300"
                  >
                    {part}
                  </span>
                );
              }
              return part;
            })}
            {lIdx < lines.length - 1 && "\n"}
          </span>
        );
      })}
    </span>
  );
}

function MediaThumb({
  item,
  className,
  contain,
  onOpen,
}: {
  item: MediaItem;
  className: string;
  contain?: boolean;
  onOpen?: (url: string) => void;
}) {
  const src = useResolvedMediaUrl(item.url);
  const [imgError, setImgError] = useState(false);

  if (item.kind !== "image") {
    return (
      <video
        src={src}
        controls
        preload="metadata"
        className={`${className} ${contain ? "h-auto w-full" : "max-h-48"}`}
      />
    );
  }

  if (imgError) {
    return (
      <div
        className={`flex items-center justify-center bg-soft p-4 rounded-lg border border-line text-ink-3 ${className}`}
      >
        <div className="flex flex-col items-center gap-1 text-center">
          <ImageIcon className="h-5 w-5 text-ink-3" />
          <span className="text-[10px] font-mono">Media Header Unavailable</span>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`group relative block overflow-hidden ${className}`}
      onClick={() => onOpen?.(src)}
      aria-label="View full image"
    >
      <img
        src={src}
        alt=""
        onError={() => setImgError(true)}
        className={contain ? "h-auto w-full" : "h-full max-h-48 w-full object-cover"}
      />
      <span className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition group-hover:bg-black/30 group-hover:opacity-100">
        <ZoomIn className="h-5 w-5 text-white" />
      </span>
    </button>
  );
}

function HeaderMedia({
  items,
  onOpen,
}: {
  items: MediaItem[];
  onOpen?: (url: string) => void;
}) {
  if (items.length === 0) return null;
  const [first, ...rest] = items;
  return (
    <div>
      <MediaThumb item={first} className="w-full" contain onOpen={onOpen} />
      {rest.length > 0 && (
        <div className="flex gap-1 overflow-x-auto p-1">
          {rest.map((m, k) => (
            <div key={k} className="h-14 w-14 shrink-0 overflow-hidden rounded-md bg-black">
              <MediaThumb item={m} className="h-full w-full object-cover" onOpen={onOpen} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CarouselCardThumb({
  file,
  url,
  alt,
  onOpen,
}: {
  file?: File;
  url?: string;
  alt: string;
  onOpen: (url: string) => void;
}) {
  const [localUrl, setLocalUrl] = useState("");
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (!file) return;
    const u = URL.createObjectURL(file);
    setLocalUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const resolvedRemote = useResolvedMediaUrl(!file ? url || "" : "");
  const src = file ? localUrl : resolvedRemote;
  if (!src || imgError) {
    return (
      <div
        className="flex h-24 w-full flex-col items-center justify-center gap-1 text-xs font-semibold text-white p-2 text-center"
        style={{ background: "linear-gradient(135deg, #f59e0b, #ef4444)" }}
      >
        <ImageIcon className="h-4 w-4" />
        <span className="truncate max-w-full text-[10px]">{alt}</span>
      </div>
    );
  }
  return (
    <button
      type="button"
      className="group relative block h-24 w-full overflow-hidden"
      onClick={() => onOpen(src)}
      aria-label={`View ${alt} full image`}
    >
      <img
        src={src}
        alt=""
        onError={() => setImgError(true)}
        className="h-24 w-full object-cover"
      />
      <span className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition group-hover:bg-black/30 group-hover:opacity-100">
        <ZoomIn className="h-5 w-5 text-white" />
      </span>
    </button>
  );
}

function ImageLightbox({ url, onClose }: { url: string; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6 backdrop-blur-xs"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Full-size image"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
      >
        <X className="h-5 w-5" />
      </button>
      <img
        src={url}
        alt="Header full size"
        className="max-h-full max-w-full rounded-xl object-contain shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

// Meta-style body formatting toolbar
function BodyToolbar({
  bodyRef,
  body,
  vars,
  onChange,
}: {
  bodyRef: React.RefObject<HTMLTextAreaElement | null>;
  body: string;
  vars: number[];
  onChange: (next: string) => void;
}) {
  const [showEmoji, setShowEmoji] = useState(false);

  const insertAtCursor = (text: string) => {
    const el = bodyRef.current;
    if (!el) {
      onChange((body + text).slice(0, 1024));
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = (body.slice(0, start) + text + body.slice(end)).slice(0, 1024);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = Math.min(start + text.length, next.length);
      el.setSelectionRange(pos, pos);
    });
  };

  const wrapSelection = (before: string, after: string) => {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const sel = body.slice(start, end) || "text";
    const next = (body.slice(0, start) + before + sel + after + body.slice(end)).slice(0, 1024);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + before.length, start + before.length + sel.length);
    });
  };

  const addVariable = () => {
    const nextNum = vars.length > 0 ? Math.max(...vars) + 1 : 1;
    insertAtCursor(`{{${nextNum}}}`);
  };

  const onEmojiClick = (data: EmojiClickData) => {
    insertAtCursor(data.emoji);
    setShowEmoji(false);
  };

  const btn =
    "flex h-7 w-7 items-center justify-center rounded-md text-xs font-semibold text-ink-2 hover:bg-soft hover:text-ink transition";
  return (
    <div className="relative flex flex-wrap items-center justify-between gap-1">
      <div className="flex items-center gap-0.5">
      <div className="relative">
        <button
          type="button"
          className={btn}
          title="Insert Emoji"
          onClick={() => setShowEmoji((v) => !v)}
        >
          😊
        </button>
        {showEmoji && (
          <div className="absolute bottom-9 left-0 z-30 shadow-card">
            <EmojiPicker
              onEmojiClick={onEmojiClick}
              width={320}
              height={380}
              searchDisabled={false}
              previewConfig={{ showPreview: false }}
            />
          </div>
        )}
      </div>
      <button
        type="button"
        className={`${btn} font-bold`}
        title="Bold (*text*)"
        onClick={() => wrapSelection("*", "*")}
      >
        B
      </button>
      <button
        type="button"
        className={`${btn} italic font-serif`}
        title="Italic (_text_)"
        onClick={() => wrapSelection("_", "_")}
      >
        I
      </button>
      <button
        type="button"
        className={`${btn} line-through`}
        title="Strikethrough (~text~)"
        onClick={() => wrapSelection("~", "~")}
      >
        S
      </button>
      <button
        type="button"
        className={`${btn} font-mono text-[10px]`}
        title="Monospace (```text```)"
        onClick={() => wrapSelection("```", "```")}
      >
        &lt;/&gt;
      </button>
      </div>
      <button
        type="button"
        title="Add variable"
        onClick={addVariable}
        className="inline-flex items-center gap-1 rounded-md border border-line/80 bg-paper px-2.5 py-1 text-xs font-semibold text-ink-2 shadow-2xs hover:bg-soft hover:text-ink transition dark:bg-[#1f1c1a]"
      >
        + Add variable
      </button>
    </div>
  );
}

export function TemplateEditorPage() {
  const { templateId } = useParams();
  const isNew = !templateId || templateId === "new";
  const { token, user } = useAuth() as any;
  const clientId = user?.client_id || user?.id || "";
  const toast = useToast();
  const cache = useQueryClient();
  const navigate = useNavigate();
  const openMobileNav = useNavStore((s) => s.openMobileNav);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const initialKind: "normal" | "carousel" =
    isNew &&
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("kind") === "carousel"
      ? "carousel"
      : "normal";

  const [draft, setDraft] = useState<TemplateDraft>(() => emptyDraft(initialKind));
  const isCarousel = draft.template_kind === "carousel";
  const [loaded, setLoaded] = useState(isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [lightbox, setLightbox] = useState<string | null>(null);

  const [localMedia, setLocalMedia] = useState<{ file: File; url: string; kind: "image" | "video" }[]>(
    [],
  );
  const localRef = useRef<{ url: string }[]>([]);
  localRef.current = localMedia;
  useEffect(
    () => () => {
      localRef.current.forEach((m) => URL.revokeObjectURL(m.url));
    },
    [],
  );

  const remoteMedia: MediaItem[] = [
    ...(draft.header_media_urls ?? []),
    ...(draft.header_media_url && !(draft.header_media_urls ?? []).includes(draft.header_media_url)
      ? [draft.header_media_url]
      : []),
  ].map((url) => ({ url, kind: kindOfUrl(url) }));

  const localPreview: MediaItem[] = localMedia.map((m) => ({ url: m.url, kind: m.kind }));
  const allMedia: MediaItem[] =
    localPreview.length > 0 ? localPreview.slice(0, 1) : remoteMedia.slice(0, 1);

  const list = useQuery({
    queryKey: ["whatsapp-templates", token],
    queryFn: templatesApi.list,
    enabled: Boolean(token),
  });

  useEffect(() => {
    if (isNew || !list.data) return;
    const found = list.data.find((t) => t.template_id === templateId);
    if (!found) return;
    setDraft({
      name: found.name,
      type: found.type,
      category: found.category,
      language: found.language,
      body: found.body,
      header_media_url: found.header_media_url || "",
      header_media_urls: found.header_media_urls ?? [],
      buttons: (found.buttons || []).map((b) => ({ ...b })),
      template_kind: found.template_kind ?? "normal",
      cards: (found.cards || []).map((c) => ({ ...c, buttons: [...(c.buttons || [])] })),
    });
    setLoaded(true);
    setDirty(false);
  }, [isNew, list.data, templateId]);

  const patch = (part: Partial<TemplateDraft>) => {
    setDraft((d) => ({ ...d, ...part }));
    setDirty(true);
    setError("");
  };
  const patchButton = (i: number, part: Partial<TemplateButton>) =>
    patch({ buttons: draft.buttons.map((b, j) => (j === i ? { ...b, ...part } : b)) });

  const vars = templateVariables(draft.body);
  const META_BODY_MAX = 1024;
  const bodyLen = draft.body.length;
  const isOverMeta = bodyLen > META_BODY_MAX;

  // Interactive preview simulation
  type SimCard = Pick<
    WhatsAppTemplate,
    "name" | "type" | "body" | "header_media_url" | "header_media_urls" | "buttons"
  >;
  const [simThread, setSimThread] = useState<
    { payload: string; response: string; card?: SimCard | null }[]
  >([]);

  const simReply = (b: TemplateButton) => {
    if (b.type === "TEMPLATE") {
      const target = (b.value || "").trim();
      const found = (list.data ?? []).find((t) => t.template_id === target || t.name === target);
      setSimThread((prev) => [
        ...prev,
        {
          payload: b.text.trim(),
          response: found ? "" : `Template "${target || b.text.trim()}" not found in library.`,
          card: found
            ? {
                name: found.name,
                type: found.type,
                body: found.body,
                header_media_url: found.header_media_url,
                header_media_urls: found.header_media_urls,
                buttons: found.buttons,
              }
            : null,
        },
      ]);
      return;
    }
    const payload =
      b.type === "QUICK_REPLY" ? b.text.trim() : (b.value || "").trim() || b.text.trim();
    const response = /stop/i.test(b.text)
      ? "You have been unsubscribed from promotions. Reply START to subscribe again."
      : `Thanks! Your reply was received. Payload: ${payload}`;
    setSimThread((prev) => [...prev, { payload, response }]);
  };

  useEffect(() => {
    setSimThread([]);
  }, [draft.buttons, draft.body, draft.header_media_url, draft.header_media_urls, localMedia]);

  const clearLocal = () => {
    localRef.current.forEach((m) => URL.revokeObjectURL(m.url));
    setLocalMedia([]);
  };

  const chooseFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const isVideo = draft.type === "VIDEO";
    const file = Array.from(files)[0];
    if (isVideo) {
      if (file.type !== "video/mp4") {
        setError("Header video must be MP4");
        return;
      }
      if (file.size > 16 * 1024 * 1024) {
        setError("Video is larger than 16 MB");
        return;
      }
    } else {
      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
        setError("Header image must be JPEG, PNG or WebP");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError("Image is larger than 5 MB");
        return;
      }
    }
    localRef.current.forEach((m) => URL.revokeObjectURL(m.url));
    setLocalMedia([{ file, url: URL.createObjectURL(file), kind: isVideo ? "video" : "image" }]);
    setDirty(true);
    setError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeLocal = (url: string) => {
    const target = localRef.current.find((m) => m.url === url);
    if (target) URL.revokeObjectURL(target.url);
    setLocalMedia((prev) => prev.filter((m) => m.url !== url));
    setDirty(true);
  };

  const removeRemote = (url: string) => {
    patch({
      header_media_urls: (draft.header_media_urls ?? []).filter((u) => u !== url),
      header_media_url: draft.header_media_url === url ? "" : draft.header_media_url,
    });
  };

  // Carousel cards
  const [cardFiles, setCardFiles] = useState<Record<number, File>>({});
  const patchCard = (i: number, part: Partial<CarouselCard>) =>
    patch({ cards: (draft.cards || []).map((c, j) => (j === i ? { ...c, ...part } : c)) });
  const patchCardButton = (i: number, bi: number, part: Partial<TemplateButton>) =>
    patch({
      cards: (draft.cards || []).map((c, j) =>
        j === i ? { ...c, buttons: c.buttons.map((b, k) => (k === bi ? { ...b, ...part } : b)) } : c,
      ),
    });

  const chooseCardFile = (i: number, files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = Array.from(files)[0];
    const wantVideo = (draft.cards[i]?.header_format || "IMAGE") === "VIDEO";
    if (wantVideo) {
      if (file.type !== "video/mp4") {
        setError(`Card ${i + 1}: header video must be MP4`);
        return;
      }
      if (file.size > 16 * 1024 * 1024) {
        setError(`Card ${i + 1}: video larger than 16 MB`);
        return;
      }
    } else {
      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
        setError(`Card ${i + 1}: header image must be JPEG, PNG or WebP`);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError(`Card ${i + 1}: image larger than 5 MB`);
        return;
      }
    }
    setCardFiles((prev) => ({ ...prev, [i]: file }));
    setDirty(true);
    setError("");
  };

  const save = async () => {
    if (!/^[a-z][a-z0-9_]{0,63}$/.test(draft.name.trim())) {
      setError("Name must start with a lowercase letter and use only a–z, 0–9 and _");
      return;
    }
    if (!draft.body.trim()) {
      setError("Body is required");
      return;
    }
    if (isCarousel) {
      const cards = draft.cards || [];
      if (cards.length < 2 || cards.length > 10) {
        setError("Carousel needs 2-10 cards");
        return;
      }
      for (let i = 0; i < cards.length; i++) {
        const c = cards[i];
        if (!c.header_media_url && !cardFiles[i]) {
          setError(`Card ${i + 1} needs a header image/video`);
          return;
        }
        if ((c.body || "").length > 160) {
          setError(`Card ${i + 1} body exceeds 160 characters`);
          return;
        }
        if ((c.buttons || []).length > 2) {
          setError(`Card ${i + 1} can have at most 2 buttons`);
          return;
        }
        for (const b of c.buttons) {
          if (!b.text.trim()) {
            setError(`Card ${i + 1}: every button needs text`);
            return;
          }
          if ((b.type === "URL" || b.type === "PHONE_NUMBER") && !(b.value || "").trim()) {
            setError(`Card ${i + 1}: ${b.type} buttons need a value`);
            return;
          }
          if (b.type === "TEMPLATE") {
            setError(`Card ${i + 1}: linked-template buttons not allowed in carousel`);
            return;
          }
        }
      }
      setSaving(true);
      setError("");
      try {
        setUploading(true);
        const nextCards = [...cards];
        try {
          for (let i = 0; i < nextCards.length; i++) {
            const f = cardFiles[i];
            if (f) {
              const { url } = await templatesApi.uploadHeaderImage(f);
              nextCards[i] = { ...nextCards[i], header_media_url: url };
            }
          }
        } catch (err: any) {
          setError(`Could not save card media to S3: ${err.message || "upload failed"}`);
          return;
        } finally {
          setUploading(false);
        }
        const payload = {
          ...draft,
          name: draft.name.trim(),
          body: draft.body,
          template_kind: "carousel" as const,
          category: "MARKETING" as const,
          type: "TEXT" as const,
          buttons: [],
          cards: nextCards,
        };
        const saved = isNew
          ? await templatesApi.create(payload)
          : await templatesApi.update(templateId!, payload);
        setCardFiles({});
        setDraft((d) => ({ ...d, cards: saved.cards ?? nextCards, template_kind: "carousel" }));
        setDirty(false);
        toast.success(isNew ? "Carousel template created" : "Carousel template updated");
        cache.invalidateQueries({ queryKey: ["whatsapp-templates", token] });
        if (isNew) navigate(`/whatsapp/templates/${saved.template_id}`, { replace: true });
      } catch (err: any) {
        setError(err.message || "Could not save carousel");
      } finally {
        setSaving(false);
      }
      return;
    }

    const effectiveButtons = draft.type === "VIDEO" ? [] : draft.buttons;
    for (const b of effectiveButtons) {
      if (!b.text.trim()) {
        setError("Every button needs text");
        return;
      }
      if (
        (b.type === "URL" || b.type === "PHONE_NUMBER" || b.type === "TEMPLATE") &&
        !(b.value || "").trim()
      ) {
        setError(`${b.type} buttons need a value`);
        return;
      }
    }
    setSaving(true);
    setError("");
    try {
      const uploaded: string[] = [];
      if (localMedia.length > 0) {
        setUploading(true);
        try {
          for (const m of localMedia) {
            const { url } = await templatesApi.uploadHeaderImage(m.file);
            uploaded.push(url);
          }
        } catch (err: any) {
          setError(`Could not save media to S3: ${err.message || "upload failed"}.`);
          return;
        } finally {
          setUploading(false);
        }
      }
      const keptRemote = [...(draft.header_media_urls ?? [])];
      if (draft.header_media_url && !keptRemote.includes(draft.header_media_url)) {
        keptRemote.unshift(draft.header_media_url);
      }
      const combined = (uploaded.length > 0 ? uploaded : keptRemote).slice(0, MAX_HEADER_MEDIA);
      const payload = {
        ...draft,
        name: draft.name.trim(),
        body: draft.body,
        header_media_url: combined[0] ?? "",
        header_media_urls: combined,
        buttons: effectiveButtons,
      };
      const saved = isNew
        ? await templatesApi.create(payload)
        : await templatesApi.update(templateId!, payload);
      clearLocal();
      setDraft((d) => ({
        ...d,
        header_media_url: saved.header_media_url ?? d.header_media_url,
        header_media_urls: saved.header_media_urls ?? d.header_media_urls,
      }));
      setDirty(false);
      toast.success(isNew ? "Template created" : "Template updated");
      cache.invalidateQueries({ queryKey: ["whatsapp-templates", token] });
      if (isNew) navigate(`/whatsapp/templates/${saved.template_id}`, { replace: true });
    } catch (err: any) {
      setError(err.message || "Could not save template");
    } finally {
      setSaving(false);
    }
  };

  // Meta approval
  const savedTemplate = !isNew
    ? (list.data ?? []).find((t) => t.template_id === templateId)
    : undefined;
  const savedStatus = (savedTemplate?.status || "draft").toLowerCase();
  const [submitInstance, setSubmitInstance] = useState("");
  const [acting, setActing] = useState<"submit" | null>(null);
  const [actionMsg, setActionMsg] = useState("");
  const [dirty, setDirty] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [pendingNavPath, setPendingNavPath] = useState<string | null>(null);

  const businessNumbers = useQuery({
    queryKey: ["whatsapp-business-numbers", token],
    queryFn: () => whatsappApi.listForClient(token, clientId),
    enabled: Boolean(token) && !isNew,
    staleTime: 60000,
  });

  const businessOptions = (businessNumbers.data ?? []).filter(
    (i) => i.integration === "WHATSAPP-BUSINESS",
  );

  useEffect(() => {
    if (!submitInstance) {
      const preferred =
        businessOptions.find(
          (i) => i.instance_name === savedTemplate?.submitted_instance_name,
        ) ?? businessOptions[0];
      if (preferred) setSubmitInstance(preferred.instance_name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessOptions, savedTemplate?.submitted_instance_name]);

  const doSubmit = async () => {
    if (!templateId || isNew) return;
    if (!submitInstance) {
      setActionMsg("Pick a Cloud-API number to submit under.");
      return;
    }
    if (draft.body.length > META_BODY_MAX) {
      setActionMsg(
        `Body is ${draft.body.length} characters; Meta allows ${META_BODY_MAX} for approval.`,
      );
      return;
    }
    setActing("submit");
    setActionMsg("");
    try {
      if (dirty) {
        await save();
      }
      const updated = await templatesApi.submit(templateId, {
        instance_name: submitInstance,
      });
      setActionMsg(
        `Submitted — Meta is reviewing (PENDING).${
          (updated.meta_converted_buttons ?? []).length > 0
            ? " Meta converted some buttons to conform."
            : ""
        }`,
      );
      cache.invalidateQueries({ queryKey: ["whatsapp-templates", token] });
    } catch (err: any) {
      setActionMsg(`Submit failed: ${err.message || "Meta API error"}`);
    } finally {
      setActing(null);
    }
  };


  const handleLeave = (to = "/whatsapp/templates") => {
    if (dirty) {
      setPendingNavPath(to);
      setShowDiscardConfirm(true);
    } else {
      navigate(to);
    }
  };

  useEffect(() => {
    if (!dirty) return;
    const handleClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest("a");
      if (target && target.href && !target.target) {
        const url = new URL(target.href, window.location.origin);
        if (url.pathname !== window.location.pathname) {
          e.preventDefault();
          e.stopPropagation();
          setPendingNavPath(url.pathname + url.search);
          setShowDiscardConfirm(true);
        }
      }
    };
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    document.addEventListener("click", handleClick, true);
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      document.removeEventListener("click", handleClick, true);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [dirty]);

  return (
    <div className="space-y-5 px-4 pb-10 sm:px-5 md:px-6">
      {/* ── Top Bar: Sticky Studio Header ── */}
      <div className="sticky top-0 z-20 -mx-4 flex flex-wrap items-center justify-between gap-3 border-b border-line bg-elevated/95 px-4 py-3 shadow-xs backdrop-blur sm:-mx-5 sm:px-5 md:-mx-6 md:px-6">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <button
            type="button"
            onClick={openMobileNav}
            aria-label="Open navigation menu"
            className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-3 transition-colors hover:bg-soft hover:text-ink md:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
          <button
            type="button"
            className={secondary}
            onClick={() => handleLeave("/whatsapp/templates")}
            aria-label="Back to Template Studio"
            title="Back to Template Studio"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="h-5 w-px bg-line" />

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              {isNew ? (
                <div className="flex items-center gap-2 max-w-sm flex-1">
                  <input
                    aria-label="Template name"
                    className={`${input} font-mono text-xs`}
                    maxLength={64}
                    value={draft.name}
                    disabled={saving}
                    onChange={(e) =>
                      patch({
                        name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""),
                      })
                    }
                    placeholder="template_name_lowercase"
                  />
                </div>
              ) : (
                <div className="flex items-center gap-2 min-w-0">
                  <h1 className="truncate font-mono text-sm font-bold text-ink">
                    {draft.name}
                  </h1>
                  <CopyButton value={draft.name} />
                </div>
              )}

              {/* Status Badge */}
              <span
                className={cn(
                  "rounded-full px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase",
                  isNew
                    ? "bg-soft text-ink-3"
                    : savedStatus === "approved"
                      ? "bg-emerald-50 text-[#16a34a] border border-emerald-500/30 dark:bg-emerald-950/40"
                      : savedStatus === "pending"
                        ? "bg-amber-50 text-amber-700 border border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-400"
                        : savedStatus === "rejected"
                          ? "bg-danger-soft text-danger border border-red-500/30"
                          : "bg-soft text-ink-3",
                )}
              >
                {isNew ? "Unpublished" : savedStatus === "approved" ? "Approved" : savedStatus}
              </span>

              {isCarousel && (
                <span className="rounded-full bg-brand-soft px-2.5 py-0.5 font-mono text-[10px] font-semibold text-brand">
                  Carousel
                </span>
              )}
            </div>
            <p className="mt-0.5 text-[10px] text-ink-3">
              WhatsApp Template Studio • {isNew ? "Create new message template" : `ID: ${templateId}`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            className={secondary}
            disabled={saving}
            onClick={() => handleLeave("/whatsapp/templates")}
          >
            Cancel
          </button>
          <button
            type="button"
            className={primary}
            disabled={saving || !loaded || !dirty || (isNew && !draft.name.trim())}
            onClick={save}
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {saving ? "Saving…" : "Save Template"}
          </button>
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-xl border border-danger/30 bg-danger-soft p-4 text-xs font-semibold text-danger"
        >
          {error}
        </div>
      )}

      {/* ── Configuration Bar ── */}
      <section className="rounded-xl border border-line/60 bg-paper-card p-4 shadow-card dark:bg-[#292524] dark:shadow-card-dark sm:p-5">
        <h2 className="text-xs font-bold uppercase tracking-wider text-ink-3">
          Template Settings
        </h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <label className={label}>
            Template Format
            <input
              className={`${input} mt-1 font-mono text-xs opacity-75 cursor-not-allowed`}
              value={isCarousel ? "Carousel Template (2–10 Cards)" : "Standard Template (Single Message)"}
              disabled
            />
          </label>

          <div>
            <span className={label}>Category</span>
            <Dropdown
              ariaLabel="Template category"
              size="sm"
              value={draft.category}
              disabled={isCarousel}
              onChange={(val) => patch({ category: val as TemplateDraft["category"] })}
              options={[
                { value: "MARKETING", label: "Marketing (Offers, updates, announcements)" },
                ...(!isCarousel ? [{ value: "UTILITY", label: "Utility (Account alerts, receipts, order updates)" }] : []),
                ...(!isCarousel ? [{ value: "AUTHENTICATION", label: "Authentication (One-time passcodes, security codes)" }] : []),
              ]}
            />
          </div>

          <label className={label}>
            Language Code
            <input
              className={`${input} mt-1 text-xs font-mono`}
              maxLength={8}
              value={draft.language}
              onChange={(e) => patch({ language: e.target.value })}
              placeholder="en"
            />
          </label>
        </div>
      </section>

      {/* ── Meta Cloud-API Review & Approval (for existing templates) ── */}
      {!isNew && (
        <section className="rounded-xl border border-line/60 bg-paper-card p-4 shadow-card dark:bg-[#292524] dark:shadow-card-dark sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line/60 pb-3">
            <div>
              <h2 className="text-sm font-bold text-ink">Meta Cloud-API Approval</h2>
              <p className="mt-0.5 text-xs text-ink-3">
                Submit this template to Meta to broadcast via registered WhatsApp Business numbers.
              </p>
            </div>
            <span
              className={cn(
                "rounded-full px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase",
                savedStatus === "approved"
                  ? "bg-emerald-50 text-[#16a34a] border border-emerald-500/30 dark:bg-emerald-950/40"
                  : savedStatus === "pending"
                    ? "bg-amber-50 text-amber-700 border border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-400"
                    : savedStatus === "rejected"
                      ? "bg-danger-soft text-danger border border-red-500/30"
                      : "bg-soft text-ink-3",
              )}
            >
              Status: {savedStatus === "approved" ? "APPROVED" : savedStatus.toUpperCase()}
            </span>
          </div>

          {savedTemplate?.meta_rejection_reason && savedStatus === "rejected" && (
            <div
              role="alert"
              className="mt-3 rounded-lg border border-danger/30 bg-danger-soft p-3 text-xs text-danger"
            >
              <strong className="font-semibold">Meta Rejection Reason:</strong>{" "}
              {savedTemplate.meta_rejection_reason}
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-end gap-3">
            <div className="min-w-[240px] flex-1">
              <span className={label}>Cloud-API Number</span>
              <Dropdown
                ariaLabel="Cloud-API Number"
                size="sm"
                value={submitInstance}
                disabled={businessNumbers.isLoading || acting !== null}
                onChange={(val) => {
                  setSubmitInstance(val);
                  setActionMsg("");
                }}
                options={[
                  {
                    value: "",
                    label: businessNumbers.isLoading
                      ? "Loading Cloud-API numbers…"
                      : businessOptions.length === 0
                        ? "No Cloud-API number connected"
                        : "Select Cloud-API number…",
                  },
                  ...businessOptions.map((i) => ({
                    value: i.instance_name,
                    label: `${i.phone_number ? `+${i.phone_number}` : i.instance_name}${i.status !== "connected" ? ` (${i.status})` : ""}`,
                  })),
                ]}
              />
            </div>

            <button
              type="button"
              className={primary}
              disabled={acting !== null || !submitInstance || isOverMeta || !dirty}
              title={!dirty ? "No unsaved changes to submit" : !submitInstance ? "Select a Cloud-API number" : "Submit template to Meta for review"}
              onClick={doSubmit}
            >
              {acting === "submit" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4" />
              )}
              {acting === "submit" ? "Submitting to Meta…" : "Submit for Meta Review"}
            </button>
          </div>

          {isOverMeta && (
            <p className="mt-2 text-xs text-danger" aria-live="polite">
              Body is {bodyLen} characters; Meta allows {META_BODY_MAX} for approval. Shorten it or
              keep it local-only.
            </p>
          )}
          {actionMsg && (
            <p className="mt-2 text-xs font-medium text-ink-2" aria-live="polite">
              {actionMsg}
            </p>
          )}
        </section>
      )}

      {/* ── Main Split View: Simulator (Left) + Studio Editor (Right) ── */}
      <div className="grid items-start gap-5 xl:grid-cols-[400px_minmax(0,1fr)]">
        {/* ── Left Column: WhatsApp Live Phone Simulator ── */}
        <section className="space-y-3">
          <div className="overflow-hidden rounded-2xl border border-line/80 bg-paper-card shadow-card dark:bg-[#292524] dark:shadow-card-dark">
            {/* Phone Header Bar (WhatsApp Green) */}
            <div className="flex items-center justify-between bg-[#075e54] px-4 py-3 text-white">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white font-bold text-xs">
                  <MessageSquare className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-xs font-bold leading-tight">
                    {draft.name.trim() || "WhatsApp Business"}
                  </p>
                  <p className="flex items-center gap-1 text-[10px] text-emerald-200">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    online
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-white/80">
                <Phone className="h-3.5 w-3.5" />
                <MoreVertical className="h-3.5 w-3.5" />
              </div>
            </div>

            {/* Chat Wallpaper Canvas */}
            <div className="flex min-h-[460px] flex-col justify-between bg-[#e5ddd5]/30 p-3.5 dark:bg-[#181615]">
              {/* Message Content Container */}
              <div className="space-y-2.5 overflow-y-auto pr-1">
                <div className="my-1 text-center">
                  <span className="rounded-md bg-paper/85 px-2.5 py-0.5 text-[10px] font-medium text-ink-3 shadow-2xs dark:bg-black/40">
                    TODAY
                  </span>
                </div>

                {isCarousel ? (
                  /* ── Carousel Multi-card Card ── */
                  <div className="mr-auto w-full max-w-[95%] overflow-hidden rounded-xl border border-line/50 bg-white shadow-sm dark:bg-[#202c33]">
                    <div className="p-3">
                      <FormattedWhatsAppText text={draft.body} />
                    </div>
                    {/* Horizontal swipeable cards */}
                    <div className="flex gap-2.5 overflow-x-auto p-2.5 pt-0">
                      {(draft.cards || []).map((c, i) => (
                        <div
                          key={i}
                          className="w-44 shrink-0 overflow-hidden rounded-xl border border-line/70 bg-white shadow-2xs dark:bg-[#1f2c34]"
                        >
                          <CarouselCardThumb
                            file={cardFiles[i]}
                            url={c.header_media_url}
                            alt={`Card ${i + 1}`}
                            onOpen={setLightbox}
                          />
                          {c.body?.trim() && (
                            <p className="whitespace-pre-wrap break-words p-2.5 text-[11px] leading-relaxed text-ink">
                              {c.body}
                            </p>
                          )}
                          {(c.buttons || []).length > 0 && (
                            <div className="space-y-1 p-2 pt-0">
                              {c.buttons.map((b, j) => (
                                <button
                                  key={j}
                                  type="button"
                                  onClick={() => b.text.trim() && simReply(b)}
                                  className="w-full rounded-lg bg-[#e7f0fe] px-2 py-1.5 text-center text-[11px] font-semibold text-brand transition hover:bg-[#d5e5fd] dark:bg-brand/20 dark:text-brand"
                                >
                                  {b.text || `Button ${j + 1}`}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  /* ── Standard Single Template Card ── */
                  <div className="mr-auto w-full max-w-[92%] overflow-hidden rounded-xl border border-line/50 bg-white shadow-sm dark:bg-[#202c33]">
                    {(draft.type === "IMAGE" || draft.type === "VIDEO") && (
                      allMedia.length > 0 ? (
                        <HeaderMedia items={allMedia} onOpen={setLightbox} />
                      ) : (
                        <div
                          className="flex h-32 w-full items-center justify-center text-xs font-semibold text-white"
                          style={{ background: "linear-gradient(135deg, #f59e0b, #ef4444)" }}
                        >
                          {draft.type === "IMAGE" ? "Image Header Preview" : "Video Header Preview"}
                        </div>
                      )
                    )}

                    <div className="p-3">
                      <FormattedWhatsAppText text={draft.body} />

                      {draft.buttons.length > 0 && (
                        <div className="mt-3 space-y-1.5 border-t border-line/40 pt-2">
                          {draft.buttons.map((b, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => b.text.trim() && simReply(b)}
                              className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#e7f0fe] px-3 py-1.5 text-center text-xs font-semibold text-brand transition hover:bg-[#d5e5fd] dark:bg-brand/20 dark:text-brand"
                            >
                              {b.type === "URL" && <ExternalLink className="h-3 w-3" />}
                              {b.type === "PHONE_NUMBER" && <Phone className="h-3 w-3" />}
                              {b.type === "TEMPLATE" && <Layers className="h-3 w-3" />}
                              {b.text || `Button ${i + 1}`}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Running simulated conversation replies */}
                {simThread.length > 0 && (
                  <div className="mt-3 space-y-2 border-t border-line/40 pt-2">
                    {simThread.map((ex, i) => (
                      <div key={i} className="space-y-1.5">
                        <div
                          className="ml-auto w-fit max-w-[85%] rounded-lg p-2.5 text-xs text-[#111b21] shadow-2xs dark:bg-[#005c4b] dark:text-white"
                          style={{ background: "#d9fdd3" }}
                        >
                          <p>{ex.payload}</p>
                          <span className="mt-0.5 block text-right text-[9px] text-ink-3">
                            ✓✓ Just now
                          </span>
                        </div>
                        {ex.card ? (
                          <div className="mr-auto w-full max-w-[90%] overflow-hidden rounded-xl border border-line bg-white shadow-sm dark:bg-[#202c33]">
                            <div className="p-2.5 text-xs text-ink">
                              <p className="font-semibold text-brand mb-1">
                                [Follow-up template: {ex.card.name}]
                              </p>
                              <p className="whitespace-pre-wrap">{ex.card.body}</p>
                            </div>
                          </div>
                        ) : ex.response ? (
                          <div className="mr-auto w-fit max-w-[85%] rounded-lg border border-line/50 bg-white p-2.5 text-xs text-ink shadow-2xs dark:bg-[#202c33]">
                            {ex.response}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Simulation Footer Controls */}
              <div className="mt-3 flex items-center justify-between border-t border-line/60 bg-paper-card px-3 py-2 text-[11px] text-ink-3 dark:bg-[#292524]">
                <span>Tap buttons above to simulate replies</span>
                {simThread.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSimThread([])}
                    className="inline-flex items-center gap-1 font-semibold text-brand hover:underline"
                  >
                    <RotateCcw className="h-3 w-3" /> Reset
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ── Right Column: Studio Form & Components ── */}
        <div className="min-w-0 space-y-4">
          {!isCarousel && (
            /* ── Header Media (Standard Template) ── */
            <section className={section}>
              <div className="flex items-center justify-between border-b border-line/60 pb-2.5">
                <div className="flex items-center gap-2">
                  <UploadCloud className="h-4 w-4 text-brand" />
                  <h2 className="text-sm font-bold text-ink">Header Content</h2>
                </div>
                <span className="font-mono text-[10px] text-ink-3">Optional media</span>
              </div>

              <div className="mt-3 grid items-start gap-3 md:grid-cols-2">
                <div>
                  <span className={label}>Header Type</span>
                  <div className="mt-1">
                    <Dropdown
                      ariaLabel="Header Type"
                      size="sm"
                      value={draft.type}
                      onChange={(val) => {
                        clearLocal();
                        const nextType = val as TemplateDraft["type"];
                        patch(
                          nextType === "VIDEO"
                            ? { type: nextType, buttons: [] }
                            : { type: nextType },
                        );
                      }}
                      options={[
                        { value: "TEXT", label: "Text Only (No Media)" },
                        { value: "IMAGE", label: "Image Header (.jpg, .png, .webp ≤5 MB)" },
                        { value: "VIDEO", label: "Video Header (.mp4 ≤16 MB)" },
                      ]}
                    />
                  </div>
                  <span className="mt-1 block text-[11px] text-ink-3">
                    {draft.type === "TEXT"
                      ? "Text header only without rich media."
                      : draft.type === "IMAGE"
                        ? "Supported: .jpg, .png, .webp (max 5 MB)."
                        : "Supported: .mp4 (max 16 MB)."}
                  </span>
                </div>

                <div>
                  <span className={label}>
                    {draft.type === "VIDEO"
                      ? "Video File (.mp4 · max 16 MB)"
                      : draft.type === "IMAGE"
                        ? "Image File (.jpg, .png, .webp · max 5 MB)"
                        : "Header Media"}
                  </span>

                  <input
                    ref={fileRef}
                    type="file"
                    accept={
                      draft.type === "VIDEO"
                        ? "video/mp4"
                        : "image/jpeg,image/png,image/webp"
                    }
                    className="hidden"
                    onChange={(e) => chooseFiles(e.target.files)}
                  />

                  {draft.type === "TEXT" ? (
                    <div className="mt-1 flex items-center rounded-lg bg-soft/50 px-3 py-2 text-xs text-ink-3">
                      <span>No media needed for text header</span>
                    </div>
                  ) : localMedia.length > 0 ? (
                    <div className="mt-1 flex items-center justify-between gap-2 rounded-lg border border-line bg-paper px-3 py-2 text-xs shadow-2xs dark:bg-[#1f1c1a]">
                      <div className="flex min-w-0 items-center gap-2">
                        {draft.type === "VIDEO" ? (
                          <Video className="h-4 w-4 shrink-0 text-brand" />
                        ) : (
                          <ImageIcon className="h-4 w-4 shrink-0 text-brand" />
                        )}
                        <span className="truncate font-medium text-ink" title={localMedia[0].file.name}>
                          {localMedia[0].file.name}
                        </span>
                        <span className="shrink-0 font-mono text-[10px] text-ink-3">
                          ({(localMedia[0].file.size / 1024).toFixed(0)} KB)
                        </span>
                      </div>
                      <div className="flex shrink-0 items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => fileRef.current?.click()}
                          disabled={uploading || saving}
                          className="rounded px-2 py-0.5 text-xs font-semibold text-brand hover:bg-brand-soft transition"
                        >
                          Replace
                        </button>
                        <button
                          type="button"
                          className="rounded p-1 text-ink-3 transition hover:bg-danger-soft hover:text-danger"
                          aria-label={`Remove ${localMedia[0].file.name}`}
                          onClick={() => removeLocal(localMedia[0].url)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ) : remoteMedia.length > 0 ? (
                    <div className="mt-1 flex items-center justify-between gap-2 rounded-lg border border-line bg-paper px-3 py-2 text-xs shadow-2xs dark:bg-[#1f1c1a]">
                      <div className="flex min-w-0 items-center gap-2">
                        {draft.type === "VIDEO" ? (
                          <Video className="h-4 w-4 shrink-0 text-brand" />
                        ) : (
                          <ImageIcon className="h-4 w-4 shrink-0 text-brand" />
                        )}
                        <span
                          className="truncate font-medium text-ink"
                          title={fileNameFromUrl(remoteMedia[0].url)}
                        >
                          {fileNameFromUrl(remoteMedia[0].url) ||
                            (draft.type === "VIDEO" ? "Video header" : "Image header")}
                        </span>
                      </div>
                      <div className="flex shrink-0 items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => fileRef.current?.click()}
                          disabled={uploading || saving}
                          className="rounded px-2 py-0.5 text-xs font-semibold text-brand hover:bg-brand-soft transition"
                        >
                          Replace
                        </button>
                        <button
                          type="button"
                          className="rounded p-1 text-ink-3 transition hover:bg-danger-soft hover:text-danger"
                          aria-label="Remove saved media"
                          onClick={() => removeRemote(remoteMedia[0].url)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading || saving}
                      className="mt-1 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-line bg-soft/40 py-2 text-xs font-semibold text-ink-2 hover:border-brand hover:bg-brand-soft/30 hover:text-brand transition dark:bg-[#1f1c1a]/50"
                    >
                      <UploadCloud className="h-4 w-4" />
                      <span>Upload {draft.type === "VIDEO" ? "Video (≤16 MB)" : "Image (≤5 MB)"}</span>
                    </button>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* ── Message Body Section ── */}
          <section className={section}>
            <div className="flex items-center justify-between border-b border-line/60 pb-2.5">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-brand" />
                <h2 className="text-sm font-bold text-ink">Message Body</h2>
              </div>
              <span
                className={cn(
                  "font-mono text-xs",
                  isOverMeta ? "text-danger font-bold" : "text-ink-3",
                )}
              >
                {bodyLen} / {META_BODY_MAX}
              </span>
            </div>

            <div className="mt-3 overflow-hidden rounded-xl border border-line bg-paper shadow-2xs focus-within:border-brand focus-within:ring-1 focus-within:ring-brand/30 transition dark:bg-[#1f1c1a]">
              <textarea
                ref={bodyRef}
                aria-label="Template body"
                rows={4}
                maxLength={META_BODY_MAX}
                value={draft.body}
                onChange={(e) => patch({ body: e.target.value })}
                placeholder="Hi {{1}}, flat {{2}}% off on your consultation this weekend!"
                className="w-full resize-y bg-transparent px-3.5 py-2.5 font-sans text-xs sm:text-sm leading-relaxed text-ink outline-none placeholder:text-ink-3 min-h-[84px]"
              />

              <div className="border-t border-line/60 bg-soft/40 px-2.5 py-1.5 dark:bg-[#252220]">
                <BodyToolbar
                  bodyRef={bodyRef}
                  body={draft.body}
                  vars={vars}
                  onChange={(body) => patch({ body })}
                />
              </div>
            </div>

            <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2 text-[11px] text-ink-3">
              <div>
                {vars.length > 0 ? (
                  <span className="inline-flex items-center gap-1">
                    Variables:{" "}
                    {vars.map((v) => (
                      <span
                        key={v}
                        className="rounded bg-brand/10 px-1.5 py-0.2 font-mono text-[10px] font-semibold text-brand"
                      >
                        {`{{${v}}}`}
                      </span>
                    ))}
                  </span>
                ) : (
                  <span>Use {"{{1}}"}, {"{{2}}"} to personalize with customer names, order IDs, etc.</span>
                )}
              </div>
              <span className="font-mono text-[10px]">Meta syntax: *bold* _italic_ ~strike~ ```code```</span>
            </div>
          </section>

          {isCarousel ? (
            /* ── Carousel Cards Section (2-10 cards) ── */
            <section className={section}>
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line/60 pb-3">
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-amber-600" />
                  <h2 className="text-sm font-bold text-ink">
                    Carousel Cards ({(draft.cards || []).length} / 10)
                  </h2>
                </div>

                <div className="flex gap-2">
                  {(draft.cards || []).length > 2 && (
                    <button
                      type="button"
                      className={secondary}
                      onClick={() => {
                        patch({ cards: draft.cards.slice(0, -1) });
                        setCardFiles((prev) => {
                          const n = { ...prev };
                          delete n[draft.cards.length - 1];
                          return n;
                        });
                      }}
                    >
                      − Remove Last
                    </button>
                  )}
                  {(draft.cards || []).length < 10 && (
                    <button
                      type="button"
                      className={secondary}
                      onClick={() =>
                        patch({ cards: [...(draft.cards || []), emptyCard()] })
                      }
                    >
                      + Add Card
                    </button>
                  )}
                </div>
              </div>

              <p className="mt-2 text-xs text-ink-3">
                Meta Carousel requires 2–10 cards of identical structure. Each card can hold up to 2
                buttons and ≤160 characters body text.
              </p>

              <div className="mt-4 space-y-4">
                {(draft.cards || []).map((c, i) => (
                  <div
                    key={i}
                    className="space-y-3 rounded-xl border border-line/80 bg-soft/30 p-4 transition hover:border-brand/40"
                  >
                    <div className="flex items-center justify-between border-b border-line/50 pb-2">
                      <h3 className="font-bold text-xs text-ink">Card #{i + 1}</h3>
                      <div className="flex items-center gap-2">
                        <div className="w-36">
                          <Dropdown
                            ariaLabel={`Card ${i + 1} header format`}
                            size="sm"
                            value={c.header_format}
                            onChange={(val) =>
                              patchCard(i, {
                                header_format: val as "IMAGE" | "VIDEO",
                              })
                            }
                            options={[
                              { value: "IMAGE", label: "Image Header" },
                              { value: "VIDEO", label: "Video Header" },
                            ]}
                          />
                        </div>
                        {(draft.cards || []).length > 2 && (
                          <button
                            type="button"
                            className="rounded p-1 text-danger hover:bg-red-500/10"
                            onClick={() =>
                              patch({ cards: draft.cards.filter((_, j) => j !== i) })
                            }
                            aria-label={`Remove card ${i + 1}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-line bg-paper px-3 py-1.5 text-xs font-semibold text-ink shadow-2xs hover:bg-soft dark:bg-[#1f1c1a]">
                        <UploadCloud className="h-3.5 w-3.5" />
                        {cardFiles[i]?.name ||
                          (c.header_media_url ? "Replace Media" : "Choose Media")}
                        <input
                          type="file"
                          className="hidden"
                          accept={
                            c.header_format === "VIDEO"
                              ? "video/mp4"
                              : "image/jpeg,image/png,image/webp"
                          }
                          onChange={(e) => chooseCardFile(i, e.target.files)}
                        />
                      </label>
                      {cardFiles[i] && (
                        <span className="text-xs text-ink-2">
                          {cardFiles[i].name} ({(cardFiles[i].size / 1024).toFixed(0)} KB) —
                          pending save
                        </span>
                      )}
                      {!cardFiles[i] && c.header_media_url && (
                        <span className="max-w-[200px] truncate font-mono text-[11px] text-ink-3">
                          {c.header_media_url.split("/").pop()}
                        </span>
                      )}
                    </div>

                    <label className={label}>
                      Card Body (≤160 characters)
                      <textarea
                        className={`${input} mt-1`}
                        rows={2}
                        maxLength={160}
                        value={c.body || ""}
                        onChange={(e) => patchCard(i, { body: e.target.value })}
                        placeholder="Highlight details for this product or service..."
                      />
                    </label>

                    {/* Card buttons */}
                    <div>
                      <div className="flex items-center justify-between pb-1">
                        <span className="text-xs font-semibold text-ink-2">
                          Card Action Buttons ({(c.buttons || []).length} / 2)
                        </span>
                        {(c.buttons || []).length < 2 && (
                          <button
                            type="button"
                            className={`${secondary} py-1 text-[11px]`}
                            onClick={() =>
                              patchCard(i, {
                                buttons: [
                                  ...(c.buttons || []),
                                  { type: "QUICK_REPLY", text: "", value: "" },
                                ],
                              })
                            }
                          >
                            + Add Button
                          </button>
                        )}
                      </div>

                      {(c.buttons || []).map((b, bi) => (
                        <div key={bi} className="mt-1 flex gap-1.5 items-center">
                          <div className="w-32 shrink-0">
                            <Dropdown
                              ariaLabel="Card button type"
                              size="sm"
                              value={b.type}
                              onChange={(val) =>
                                patchCardButton(i, bi, {
                                  type: val as TemplateButton["type"],
                                })
                              }
                              options={[
                                { value: "QUICK_REPLY", label: "Quick Reply" },
                                { value: "URL", label: "Website URL" },
                                { value: "PHONE_NUMBER", label: "Phone Number" },
                              ]}
                            />
                          </div>
                          <input
                            className={input}
                            maxLength={25}
                            value={b.text}
                            onChange={(e) => patchCardButton(i, bi, { text: e.target.value })}
                            placeholder="Button label (≤25)"
                          />
                          {(b.type === "URL" || b.type === "PHONE_NUMBER") && (
                            <input
                              className={input}
                              value={b.value || ""}
                              onChange={(e) => patchCardButton(i, bi, { value: e.target.value })}
                              placeholder={b.type === "URL" ? "https://…" : "+91…"}
                            />
                          )}
                          <button
                            type="button"
                            className="rounded p-1.5 text-danger hover:bg-red-500/10"
                            onClick={() =>
                              patchCard(i, {
                                buttons: c.buttons.filter((_, k) => k !== bi),
                              })
                            }
                            aria-label="Remove button"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : draft.type === "VIDEO" ? null : (
            /* ── Footer Buttons Section (Standard Template) ── */
            <section className={section}>
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line/60 pb-3">
                <div>
                  <h2 className="text-sm font-bold text-ink">Interactive Buttons</h2>
                  <p className="mt-0.5 text-xs text-ink-3">
                    Add quick reply chips, website links, or call triggers (up to 5 buttons).
                  </p>
                </div>
                {draft.buttons.length < 5 && (
                  <button
                    type="button"
                    className={secondary}
                    onClick={() =>
                      patch({
                        buttons: [
                          ...draft.buttons,
                          { type: "QUICK_REPLY", text: "", value: "" },
                        ],
                      })
                    }
                  >
                    <Plus className="h-3.5 w-3.5" /> Add Button
                  </button>
                )}
              </div>

              {draft.buttons.length === 0 && (
                <div className="rounded-xl border border-dashed border-line p-6 text-center text-xs text-ink-3">
                  No buttons added yet. Click &ldquo;+ Add Button&rdquo; to add interactive actions.
                </div>
              )}

              {draft.buttons.length > 0 && (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-line font-mono text-[10px] uppercase tracking-wider text-ink-3">
                        <th className="py-2 px-1">#</th>
                        <th className="py-2 px-2">Action Type</th>
                        <th className="py-2 px-2">Button Label</th>
                        <th className="py-2 px-2">Target Action</th>
                        <th className="py-2 px-1">
                          <span className="sr-only">Remove</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {draft.buttons.map((b, i) => (
                        <tr key={i} className="border-b border-line/50 last:border-0">
                          <td className="py-2 px-1 font-mono text-ink-3">{i + 1}</td>
                          <td className="min-w-36 py-2 px-2">
                            <Dropdown
                              ariaLabel={`Button ${i + 1} type`}
                              size="sm"
                              value={b.type}
                              onChange={(val) =>
                                patchButton(i, {
                                  type: val as TemplateButton["type"],
                                })
                              }
                              options={[
                                { value: "QUICK_REPLY", label: "Quick Reply" },
                                { value: "URL", label: "Website Link (URL)" },
                                { value: "PHONE_NUMBER", label: "Phone Call" },
                                { value: "TEMPLATE", label: "Follow-up Template" },
                              ]}
                            />
                          </td>
                          <td className="min-w-36 py-2 px-2">
                            <input
                              aria-label={`Button ${i + 1} text`}
                              className={input}
                              maxLength={25}
                              value={b.text}
                              onChange={(e) => patchButton(i, { text: e.target.value })}
                              placeholder={i === 0 ? "Book appointment" : "Visit site"}
                            />
                          </td>
                          <td className="min-w-44 py-2 px-2">
                            {b.type === "TEMPLATE" ? (
                              (() => {
                                const targets = (list.data ?? []).filter(
                                  (t) => t.template_id !== templateId,
                                );
                                const current = (b.value || "").trim();
                                const known = targets.some((t) => t.name === current);
                                return (
                                  <Dropdown
                                    ariaLabel={`Button ${i + 1} response`}
                                    size="sm"
                                    searchable
                                    value={known ? current : ""}
                                    onChange={(val) => patchButton(i, { value: val })}
                                    options={[
                                      { value: "", label: "Link library template…" },
                                      ...targets.map((t) => ({
                                        value: t.name,
                                        label: t.name,
                                      })),
                                      ...(!known && current ? [{ value: current, label: current }] : []),
                                    ]}
                                  />
                                );
                              })()
                            ) : (
                              <input
                                aria-label={`Button ${i + 1} response`}
                                className={input}
                                maxLength={500}
                                value={b.value || ""}
                                onChange={(e) => patchButton(i, { value: e.target.value })}
                                placeholder={
                                  b.type === "URL"
                                    ? "https://yourwebsite.com"
                                    : b.type === "PHONE_NUMBER"
                                      ? "+919876543210"
                                      : "Quick reply payload"
                                }
                              />
                            )}
                          </td>
                          <td className="py-2 px-1">
                            <button
                              type="button"
                              className="rounded p-1.5 text-ink-3 transition hover:bg-danger-soft hover:text-danger"
                              aria-label={`Remove button ${i + 1}`}
                              onClick={() =>
                                patch({
                                  buttons: draft.buttons.filter((_, j) => j !== i),
                                })
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </div>
      </div>

      {lightbox && <ImageLightbox url={lightbox} onClose={() => setLightbox(null)} />}

      {showDiscardConfirm && (
        <ConfirmModal
          title="Discard unsaved changes?"
          body="You have unsaved changes to this template. Are you sure you want to discard them?"
          confirmLabel="Discard"
          onClose={() => {
            setShowDiscardConfirm(false);
            setPendingNavPath(null);
          }}
          onConfirm={() => {
            setShowDiscardConfirm(false);
            setDirty(false);
            navigate(pendingNavPath || "/whatsapp/templates");
          }}
        />
      )}
    </div>
  );
}
